import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Plus, TrendingUp, Utensils, Flame, Receipt, Pill, Wallet, CalendarDays, FileText, Target,
} from "lucide-react";
import api from "../lib/api";
import { money } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useAutoSync } from "../hooks/useAutoSync";
import FoodDialog from "../components/FoodDialog";
import MonthlyReport from "../components/MonthlyReport";
import { Button } from "../components/ui/button";

function KpiCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div data-testid={testid}
      className={`rounded-2xl border p-4 ${accent ? "border-0 bg-brand text-white" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center justify-between">
        <p className={`text-[11px] font-semibold uppercase tracking-wider ${accent ? "text-white/80" : "text-slate-400"}`}>{label}</p>
        <Icon className={`h-4 w-4 ${accent ? "text-white/80" : "text-brand"}`} />
      </div>
      <p className="mt-2 font-mono text-2xl font-extrabold">{value}</p>
      {sub && <p className={`mt-0.5 text-xs ${accent ? "text-white/70" : "text-slate-400"}`}>{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(async () => { const { data } = await api.get("/dashboard"); setD(data); }, []);
  useAutoSync(load, [load]);

  if (!d) return <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" /></div>;

  const chartData = d.chart.map((c) => ({ ...c, label: new Date(c.date + "T00:00:00").toLocaleDateString(undefined, { month: "numeric", day: "numeric" }) }));
  const supp = d.supplements || {};
  const combined = d.combined || {};

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Hey {user?.name} 👋</h1>
          <p className="text-sm text-slate-500">Here's your food spending at a glance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setReportOpen(true)} data-testid="dash-report"
            className="rounded-full border-slate-200"><FileText className="mr-1.5 h-4 w-4" />Report</Button>
          <Button variant="outline" onClick={() => navigate("/today")} data-testid="dash-log-today"
            className="rounded-full border-slate-200"><CalendarDays className="mr-1.5 h-4 w-4" />Log Today</Button>
          <Button onClick={() => setAddOpen(true)} data-testid="dash-quick-add"
            className="rounded-full bg-brand px-5 hover:bg-brand-hover"><Plus className="mr-1.5 h-4 w-4" />Add Food</Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard testid="kpi-today-cost" label="Today's Food Cost" value={money(d.today_cost, cur)} icon={Flame} accent />
        <KpiCard testid="kpi-avg-daily" label="Avg Daily Cost" value={money(d.avg_daily, cur)} icon={Wallet} />
        <KpiCard testid="kpi-weekly" label="Projected Weekly" value={money(d.projected_weekly, cur)} sub="avg × 7" icon={TrendingUp} />
        <KpiCard testid="kpi-monthly" label="Projected Monthly" value={money(d.projected_monthly, cur)} sub="avg × 30" icon={TrendingUp} />
      </div>

      {/* Budget goal */}
      {d.monthly_budget > 0 ? (() => {
        const spent = d.grocery_month_spend || 0;
        const budget = d.monthly_budget;
        const pct = Math.min(100, (spent / budget) * 100);
        const over = spent > budget;
        const barColor = over ? "bg-rose-500" : pct >= 75 ? "bg-amber-500" : "bg-emerald-500";
        return (
          <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="budget-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Target className="h-4 w-4 text-brand" />Monthly Food Budget</div>
              <button onClick={() => navigate("/settings")} className="text-xs font-semibold text-brand hover:underline">Edit</button>
            </div>
            <div className="mt-3 flex items-end justify-between">
              <span className="font-mono text-2xl font-extrabold" data-testid="budget-spent">{money(spent, cur)}</span>
              <span className="font-mono text-sm text-slate-400">of {money(budget, cur)}</span>
            </div>
            <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.max(3, pct)}%` }} />
            </div>
            <p className={`mt-2 text-xs font-semibold ${over ? "text-rose-600" : "text-slate-500"}`} data-testid="budget-remaining">
              {over ? `${money(spent - budget, cur)} over budget` : `${money(budget - spent, cur)} left this month`}
            </p>
          </div>
        );
      })() : (
        <button onClick={() => navigate("/settings")} data-testid="set-budget-prompt"
          className="mt-3 flex w-full items-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm font-semibold text-slate-500 transition-colors hover:border-brand hover:text-brand">
          <Target className="h-4 w-4" />Set a monthly food budget to track your spending
        </button>
      )}

      {/* Chart */}
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="spending-chart">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold">Spending over time</h3>
          <span className="text-xs text-slate-400">last 30 days</span>
        </div>
        <div className="mt-4 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ left: -20, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="gFood" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FA7247" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#FA7247" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gSupp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} interval={4} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={48} />
              <Tooltip formatter={(v, n) => [money(v, cur), n === "food" ? "Food" : "Supplement"]}
                contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12, fontFamily: "JetBrains Mono" }} />
              <Area type="monotone" dataKey="food" stroke="#FA7247" strokeWidth={2.5} fill="url(#gFood)" />
              <Area type="monotone" dataKey="supplement" stroke="#8B5CF6" strokeWidth={2} fill="url(#gSupp)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Highlights */}
        <div className="space-y-3 lg:col-span-1">
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="most-expensive-meal">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Utensils className="h-4 w-4 text-brand" />Most Expensive Meal</div>
            {d.most_expensive_meal ? (
              <div className="mt-2 flex items-center justify-between">
                <span className="font-display font-bold">{d.most_expensive_meal.name}</span>
                <span className="font-mono font-extrabold text-brand">{money(d.most_expensive_meal.total_cost, cur)}</span>
              </div>
            ) : <p className="mt-2 text-sm text-slate-300">No meals yet</p>}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="most-expensive-food">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Flame className="h-4 w-4 text-brand" />Priciest Food / Serving</div>
            {d.most_expensive_food ? (
              <div className="mt-2 flex items-center justify-between">
                <span className="font-display font-bold">{d.most_expensive_food.name}</span>
                <span className="font-mono font-extrabold text-brand">{money(d.most_expensive_food.serving_cost, cur)}</span>
              </div>
            ) : <p className="mt-2 text-sm text-slate-300">No foods yet</p>}
          </div>
        </div>

        {/* Recent purchases */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 lg:col-span-2" data-testid="recent-purchases">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Receipt className="h-4 w-4 text-brand" />Recent Purchases</div>
            <button onClick={() => navigate("/purchases")} className="text-xs font-semibold text-brand hover:underline">View all</button>
          </div>
          <div className="mt-2 divide-y divide-slate-100">
            {d.recent_purchases.length === 0 && <p className="py-3 text-sm text-slate-300">No purchases yet</p>}
            {d.recent_purchases.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 text-sm">
                <div><span className="font-semibold text-slate-700">{r.food_name}</span>
                  <span className="text-xs text-slate-400"> · {r.package_qty} {r.package_unit}{r.store ? ` · ${r.store}` : ""}</span></div>
                <div className="text-right"><span className="font-mono font-semibold text-slate-700">{money(r.price, cur)}</span>
                  <p className="text-xs text-slate-400">{r.date}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Supplements + Total Nutrition Investment */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="supplement-summary">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500"><Pill className="h-4 w-4 text-violet-500" />Supplements</div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div><p className="text-[11px] uppercase tracking-wider text-slate-400">This month</p><p className="font-mono text-lg font-bold">{money(supp.month_spend || 0, cur)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-slate-400">Projected / mo</p><p className="font-mono text-lg font-bold">{money(supp.projected_monthly || 0, cur)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-slate-400">Projected / yr</p><p className="font-mono text-lg font-bold">{money(supp.projected_annual || 0, cur)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-slate-400">Priciest / mo</p><p className="truncate font-semibold">{supp.most_expensive_month ? supp.most_expensive_month.name : "—"}</p></div>
          </div>
        </div>

        <div className="rounded-2xl border-0 p-5 text-white" data-testid="total-nutrition-investment"
          style={{ background: "linear-gradient(135deg,#1E2022 0%,#3A3D42 100%)" }}>
          <div className="flex items-center gap-2 text-sm font-semibold text-white/80"><Wallet className="h-4 w-4" />Total Nutrition Investment</div>
          <p className="mt-1 text-xs text-white/50">Food + Supplements combined</p>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div><p className="text-[11px] uppercase tracking-wider text-white/50">Daily</p><p className="font-mono text-lg font-bold">{money(combined.daily || 0, cur)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-white/50">Weekly</p><p className="font-mono text-lg font-bold">{money(combined.weekly || 0, cur)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-white/50">Monthly</p><p className="font-mono text-lg font-bold">{money(combined.monthly || 0, cur)}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-white/50">Annual</p><p className="font-mono text-lg font-bold text-brand">{money(combined.annual || 0, cur)}</p></div>
          </div>
        </div>
      </div>

      {/* Mobile FAB */}
      <button onClick={() => setAddOpen(true)} data-testid="fab-add-food"
        className="fixed bottom-20 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-lg shadow-brand/40 transition-transform hover:scale-105 md:hidden">
        <Plus className="h-7 w-7" />
      </button>

      <FoodDialog open={addOpen} onOpenChange={setAddOpen} food={null} onSaved={load} />
      <MonthlyReport open={reportOpen} onOpenChange={setReportOpen} />
    </div>
  );
}
